
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash, Users, Search } from 'lucide-react';
import { useGuests, useGuestMutations, Guest } from '@/hooks/useGuests';
import GuestFormModal from './GuestFormModal';

const AdminGuestsTab = () => {
  const { data: guests = [], isLoading, error } = useGuests();
  const { deleteGuest } = useGuestMutations();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const handleDelete = async (guestId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este huésped?')) {
      setIsDeleting(guestId);
      try {
        await deleteGuest.mutateAsync(guestId);
      } finally {
        setIsDeleting(null);
      }
    }
  };

  // Filter guests based on search term
  const filteredGuests = guests.filter((guest) => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = guest.name.toLowerCase().includes(searchLower);
    const emailMatch = guest.email?.toLowerCase().includes(searchLower) || false;
    const originMatch = guest.origins?.name.toLowerCase().includes(searchLower) || false;
    
    return nameMatch || emailMatch || originMatch;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-16">
        <div className="text-gray-500">Cargando huéspedes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-medium">Error al cargar huéspedes</h3>
        <p className="text-red-600 text-sm mt-1">
          No se pudieron cargar los huéspedes. Por favor, intenta nuevamente.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">Huéspedes</h2>
          <Button 
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Agregar Huésped
          </Button>
        </div>

        {/* Search Filter */}
        <div className="flex-1 relative max-w-md">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar por nombre, correo u origen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {guests.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-gray-800 font-medium mb-2">No hay huéspedes</h3>
            <p className="text-gray-600 text-sm mb-4">
              No se encontraron huéspedes en la base de datos.
            </p>
            <Button 
              onClick={() => setIsCreateModalOpen(true)}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Crear Primer Huésped
            </Button>
          </div>
        ) : filteredGuests.length === 0 ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-gray-800 font-medium mb-2">No se encontraron resultados</h3>
            <p className="text-gray-600 text-sm mb-4">
              No hay huéspedes que coincidan con tu búsqueda "{searchTerm}".
            </p>
            <Button 
              variant="outline" 
              onClick={() => setSearchTerm('')}
            >
              Limpiar búsqueda
            </Button>
          </div>
        ) : (
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Correo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Teléfono
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Origen
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredGuests.map((guest) => (
                    <tr key={guest.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-sm font-medium text-gray-900">
                        {guest.name}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {guest.email || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {guest.phone || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        {guest.origins?.name || '-'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-900">
                        <div className="flex space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingGuest(guest)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(guest.id)}
                            disabled={isDeleting === guest.id}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <GuestFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        guest={null}
      />

      {editingGuest && (
        <GuestFormModal
          isOpen={true}
          onClose={() => setEditingGuest(null)}
          guest={editingGuest}
        />
      )}
    </>
  );
};

export default AdminGuestsTab;
